import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Target, Trophy, Users, Twitch } from "lucide-react";
import { api } from "../lib/api";
import BlogCard from "../components/BlogCard";
import TwitchEmbed from "../components/TwitchEmbed";

const HERO_BG =
  "https://images.unsplash.com/photo-1708032565079-f43e698f4db9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const LOGO =
  "https://customer-assets.emergentagent.com/job_5d53cc0c-f8e0-4354-807b-347bf58e1216/artifacts/3rlolaxv_Logo.png";

const STATS = [
  { label: "Active Players", value: "13" },
  { label: "Tournaments Entered", value: "47" },
  { label: "Podium Finishes", value: "19" },
  { label: "Games We Compete In", value: "3" },
];

const PILLARS = [
  { icon: Zap, title: "Integrity", copy: "We play fair and uphold the highest standards of sportsmanship." },
  { icon: Target, title: "Dedication", copy: "Late-night scrims, VOD reviews, tournament days — we show up." },
  { icon: Trophy, title: "Excellence", copy: "We study every clutch and every defeat to compound improvement." },
  { icon: Users, title: "Community", copy: "AXE is a family — we celebrate wins and carry each other through losses." },
];

export default function Home() {
  const [latest, setLatest] = useState([]);

  useEffect(() => {
    api.get("/blog").then((r) => setLatest(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="text-white">
      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_BG} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-mask" />
          <div className="absolute inset-0 bg-gradient-to-br from-axe-purple/15 via-transparent to-axe-tangerine/10" />
        </div>

        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-24 md:pb-40">
          <div className="max-w-3xl animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-axe-purple/50 bg-axe-purple/10 px-4 py-1.5 text-[11px] uppercase tracking-[0.3em] text-axe-purple font-bold">
              <span className="h-1.5 w-1.5 rounded-full bg-axe-tangerine animate-pulse" />
              Recruiting Season · Open
            </div>
            <h1 className="mt-6 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Forge Your <span className="text-axe-purple glow-purple">Legacy</span><br />
              Among <span className="text-axe-tangerine">AXE</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-neutral-300 max-w-xl leading-relaxed">
              Anti Xquad'E is a professional esports organization competing across Valorant, Apex
              Legends, and Call of Duty. We cut through the noise of the amateur scene to build a
              legacy in global esports.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="https://discord.gg/antixquade"
                target="_blank"
                rel="noreferrer"
                data-testid="hero-discord-cta"
                className="inline-flex items-center gap-2 rounded-full bg-axe-tangerine px-7 py-3.5 font-bold uppercase tracking-[0.22em] text-black hover:bg-axe-tangerine-hover transition-colors drop-shadow-[0_0_16px_rgba(255,140,0,0.45)] hover:drop-shadow-[0_0_28px_rgba(255,140,0,0.7)]"
              >
                Join Discord <ArrowRight size={16} />
              </a>
              <Link
                to="/recruitment"
                data-testid="hero-apply-cta"
                className="inline-flex items-center gap-2 rounded-full border-2 border-axe-purple text-axe-purple px-7 py-3 font-bold uppercase tracking-[0.22em] hover:bg-axe-purple hover:text-white transition-all"
              >
                Apply to Join
              </Link>
            </div>
          </div>
        </div>

        {/* stats strip */}
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 -mt-8 md:-mt-16 pb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 rounded-2xl border border-axe-border bg-axe-surface/90 backdrop-blur p-5 md:p-7">
            {STATS.map((s) => (
              <div key={s.label} className="text-center md:text-left">
                <div className="font-display font-black text-3xl md:text-4xl text-white">
                  {s.value}
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.28em] text-axe-purple font-bold">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GAMES BENTO */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
              Our Arenas
            </div>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl">
              Three Games. One Standard.
            </h2>
          </div>
          <Link
            to="/roster"
            className="hidden sm:inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] font-bold text-axe-purple hover:text-white"
            data-testid="home-view-roster"
          >
            View Roster <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            { name: "Valorant", accent: "from-axe-purple/40", img: "https://images.unsplash.com/photo-1542751371-adc38448a05e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", tag: "Tactical 5v5" },
            { name: "Apex Legends", accent: "from-axe-tangerine/40", img: "https://images.unsplash.com/photo-1511512578047-dfb367046420?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", tag: "Battle Royale" },
            { name: "Call of Duty", accent: "from-axe-purple/40", img: "https://images.unsplash.com/photo-1535223289827-42f1e9919769?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", tag: "FPS Competitive" },
          ].map((g) => (
            <Link
              key={g.name}
              to="/roster"
              data-testid={`home-game-card-${g.name.toLowerCase().replace(/\s/g, "-")}`}
              className="group relative overflow-hidden rounded-2xl border border-axe-border bg-axe-surface aspect-[4/5] hover:border-axe-purple/60 transition-all"
            >
              <img src={g.img} alt={g.name} className="absolute inset-0 h-full w-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500" />
              <div className={`absolute inset-0 bg-gradient-to-t ${g.accent} via-axe-surface/70 to-axe-surface`} />
              <div className="absolute inset-x-0 bottom-0 p-6">
                <div className="text-[11px] uppercase tracking-[0.3em] text-axe-tangerine font-bold">
                  {g.tag}
                </div>
                <div className="mt-2 font-display font-black text-2xl text-white">
                  {g.name}
                </div>
                <div className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-white/80 group-hover:text-white">
                  Meet the squad <ArrowRight size={14} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-24">
        <div className="grid md:grid-cols-4 gap-5">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              className="rounded-2xl bg-axe-surface border border-axe-border p-6 hover:border-axe-tangerine/60 transition-colors"
              data-testid={`home-pillar-${p.title.toLowerCase()}`}
            >
              <p.icon className="text-axe-tangerine" size={28} />
              <div className="mt-5 font-display font-bold text-xl text-white">{p.title}</div>
              <div className="mt-2 text-sm text-neutral-400 leading-relaxed">{p.copy}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TWITCH LIVE */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-24">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
              Live Now
            </div>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl">
              Watch AXE on Twitch
            </h2>
          </div>
          <a
            href="https://www.twitch.tv/antixquade"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-axe-purple px-6 py-3 text-xs uppercase tracking-[0.25em] font-bold text-white hover:bg-axe-purple-hover drop-shadow-[0_0_14px_rgba(176,38,255,0.45)]"
            data-testid="home-twitch-link"
          >
            <Twitch size={14} /> Follow Channel
          </a>
        </div>
        <TwitchEmbed channel="antixquade" height={480} />
      </section>

      {/* LATEST NEWS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
              Latest Drops
            </div>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl">News & Highlights</h2>
          </div>
          <Link
            to="/news"
            className="text-sm uppercase tracking-[0.25em] font-bold text-axe-purple hover:text-white"
            data-testid="home-view-news"
          >
            All updates
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {latest.map((p) => <BlogCard key={p.id} post={p} />)}
        </div>
      </section>

      {/* JOIN CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-24">
        <div className="relative overflow-hidden rounded-3xl border border-axe-border bg-axe-surface p-10 md:p-14 flex flex-col md:flex-row items-center gap-10">
          <img src={LOGO} alt="AXE logo" className="h-32 w-32 md:h-40 md:w-40 glow-purple" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
              Sharpen Your Edge
            </div>
            <h3 className="mt-3 font-display font-black text-3xl md:text-4xl text-white">
              Think you have what it takes?
            </h3>
            <p className="mt-4 text-neutral-400 max-w-xl">
              We're recruiting across all three titles. Drop your IGN, your rank, and why you want
              AXE on your jersey. We read every application.
            </p>
            <div className="mt-7 flex flex-wrap gap-4">
              <Link
                to="/recruitment"
                className="rounded-full bg-axe-purple px-7 py-3.5 font-bold uppercase tracking-[0.22em] text-white hover:bg-axe-purple-hover transition-colors drop-shadow-[0_0_16px_rgba(176,38,255,0.5)]"
                data-testid="home-cta-apply"
              >
                Apply Now
              </Link>
              <Link
                to="/about"
                className="rounded-full border-2 border-axe-tangerine px-7 py-3 font-bold uppercase tracking-[0.22em] text-axe-tangerine hover:bg-axe-tangerine hover:text-black transition-all"
                data-testid="home-cta-about"
              >
                Our Story
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
